import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pharmacyDrugService } from '../pharmacyDrugService';

// Drugs
export function usePharmacyDrugs(
  page = 1,
  limit = 20,
  filters?: {
    search?: string;
    categoryId?: string;
    genericNameId?: string;
    companyId?: string;
    storageRackId?: string;
    lowOnly?: boolean;
  },
) {
  return useQuery({
    queryKey: ['pharmacy-drugs', page, limit, filters],
    queryFn: () => pharmacyDrugService.getDrugs(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function usePharmacyDrug(id?: string) {
  return useQuery({
    queryKey: ['pharmacy-drug', id],
    queryFn: () => pharmacyDrugService.getDrug(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useCreatePharmacyDrug() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyDrugService.createDrug,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drugs'] });
    },
  });
}

export function useUpdatePharmacyDrug() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => pharmacyDrugService.updateDrug(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drugs'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drug', variables.id] });
    },
  });
}

export function useDeletePharmacyDrug() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyDrugService.deleteDrug,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drugs'] });
    },
  });
}

/** Bulk-delete drugs as a single silent mutation. */
export function useBulkDeletePharmacyDrugs() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await pharmacyDrugService.deleteDrug(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drugs'] });
    },
  });
}

// Categories
export function usePharmacyDrugCategories(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['pharmacy-drug-categories', page, limit, search],
    queryFn: () => pharmacyDrugService.getCategories(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 60000,
  });
}

export function useCreatePharmacyDrugCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyDrugService.createCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drug-categories'] });
    },
  });
}

export function useUpdatePharmacyDrugCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => pharmacyDrugService.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drug-categories'] });
    },
  });
}

export function useDeletePharmacyDrugCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyDrugService.deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drug-categories'] });
    },
  });
}

/**
 * Bulk-generate codes for categories. Runs as a single silent mutation
 * (meta.hideToast) so the UI shows one summary toast instead of one per item.
 */
export function useBulkGeneratePharmacyDrugCategoryCodes() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (items: { id: string; code: string }[]) => {
      for (const item of items) {
        await pharmacyDrugService.updateCategory(item.id, { code: item.code });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drug-categories'] });
    },
  });
}

/** Bulk-delete categories as a single silent mutation. */
export function useBulkDeletePharmacyDrugCategories() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await pharmacyDrugService.deleteCategory(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drug-categories'] });
    },
  });
}

// Generic Names
export function usePharmacyGenericNames(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['pharmacy-generic-names', page, limit, search],
    queryFn: () => pharmacyDrugService.getGenericNames(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 60000,
  });
}

export function useCreatePharmacyGenericName() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyDrugService.createGenericName,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-generic-names'] });
    },
  });
}

export function useUpdatePharmacyGenericName() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => pharmacyDrugService.updateGenericName(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-generic-names'] });
    },
  });
}

export function useDeletePharmacyGenericName() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyDrugService.deleteGenericName,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-generic-names'] });
    },
  });
}

/** Bulk-delete generic names as a single silent mutation. */
export function useBulkDeletePharmacyGenericNames() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await pharmacyDrugService.deleteGenericName(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-generic-names'] });
    },
  });
}

// Companies
export function usePharmacyCompanies(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['pharmacy-companies', page, limit, search],
    queryFn: () => pharmacyDrugService.getCompanies(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 60000,
  });
}

export function useCreatePharmacyCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyDrugService.createCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-companies'] });
    },
  });
}

export function useUpdatePharmacyCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => pharmacyDrugService.updateCompany(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-companies'] });
    },
  });
}

export function useDeletePharmacyCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyDrugService.deleteCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-companies'] });
    },
  });
}

/**
 * Bulk-generate codes for companies. Runs as a single silent mutation
 * (meta.hideToast) so the UI shows one summary toast instead of one per item.
 */
export function useBulkGeneratePharmacyCompanyCodes() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (items: { id: string; code: string }[]) => {
      for (const item of items) {
        await pharmacyDrugService.updateCompany(item.id, { code: item.code });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-companies'] });
    },
  });
}

/** Bulk-delete companies as a single silent mutation. */
export function useBulkDeletePharmacyCompanies() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await pharmacyDrugService.deleteCompany(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-companies'] });
    },
  });
}

// Storage Racks
export function usePharmacyStorageRacks() {
  return useQuery({
    queryKey: ['pharmacy-storage-racks'],
    queryFn: () => pharmacyDrugService.getStorageRacks(),
    staleTime: 60000,
  });
}

export function useCreatePharmacyStorageRack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyDrugService.createStorageRack,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-storage-racks'] });
    },
  });
}

export function useUpdatePharmacyStorageRack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => pharmacyDrugService.updateStorageRack(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-storage-racks'] });
    },
  });
}

export function useDeletePharmacyStorageRack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyDrugService.deleteStorageRack,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-storage-racks'] });
    },
  });
}

// Staff Assignments
export function usePharmacyStaffAssignments() {
  return useQuery({
    queryKey: ['pharmacy-staff-assignments'],
    queryFn: () => pharmacyDrugService.getStaffAssignments(),
    staleTime: 30000,
  });
}

export function useCreatePharmacyStaffAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyDrugService.createStaffAssignment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-staff-assignments'] });
    },
  });
}

export function useUpdatePharmacyStaffAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => pharmacyDrugService.updateStaffAssignment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-staff-assignments'] });
    },
  });
}

export function useDeletePharmacyStaffAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyDrugService.deleteStaffAssignment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-staff-assignments'] });
    },
  });
}

// Medicine API sync
export function useSyncPharmacyMedicines() {
  return useMutation({
    mutationFn: (options?: { queries?: string[]; clearExisting?: boolean }) =>
      pharmacyDrugService.syncMedicines(options),
  });
}

export function usePharmacySyncStatus(syncId?: string) {
  return useQuery({
    queryKey: ['pharmacy-sync-status', syncId],
    queryFn: () => pharmacyDrugService.getSyncStatus(syncId!),
    enabled: !!syncId,
    refetchInterval: (query) => {
      const status = query.state.data?.data;
      if (!status) return 1000;
      return status.state === 'completed' || status.state === 'failed' || status.state === 'stopped'
        ? false
        : 1000;
    },
  });
}

export function useStopPharmacySync() {
  return useMutation({
    mutationFn: (syncId: string) => pharmacyDrugService.stopSync(syncId),
  });
}
